import { useIntegrationApp } from "@membranehq/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { queryClient, useSlackIntegration } from "../../../hooks/QueryHooks";
import IntegrationDetailShell from "../../home/settings/integrations/components/IntegrationDetailShell";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import { getProvider } from "../../../integrations/providers";
import notify from "../../../utils/notify";
import { authorizedApiClient } from "../../../services/config";
import { runConnectionFlow } from "../../../integrations/integrationAppPostConnect";

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

const SlackPage = () => {
  const workspace: any = useWorkspaceStore((state) => state.workspace);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [localConnected, setLocalConnected] = useState(false);
  const integrationApp = useIntegrationApp();
  const provider = getProvider("slack");

  const { data: slackIntegration }: any = useSlackIntegration({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const backendConnected = slackIntegration?.connected === true;
  const isConnected = backendConnected || localConnected;

  // Check integration.app connection state on mount (and after OAuth redirects)
  // If connected in integration.app but not in our backend, sync it
  useEffect(() => {
    const checkAndSync = async () => {
      try {
        const integration = await integrationApp.integration(provider.key).get();
        const connection = integration?.connection;
        if (connection && !(connection as any).disconnected) {
          setLocalConnected(true);

          // If backend doesn't know about this connection yet, sync it
          if (!backendConnected) {
            try {
              await authorizedApiClient.post(
                `/${workspace?.id}/integrations/slack/sync-connection`,
                {
                  connection_id: (connection as any).id || (connection as any).connectionId,
                  connection_name: (connection as any).name || "Slack",
                },
              );
            } catch {
              // webhook may handle it
            }
          }
          void queryClient.invalidateQueries(["slack-integration", workspace?.id]);
        }
      } catch {
        // no existing connection
      }
    };
    if (workspace?.id) checkAndSync();
  }, [integrationApp, provider.key, workspace?.id, backendConnected]);

  const handleBack = () => {
    if (!workspace?.unique_identifier) {
      navigate(-1);
      return;
    }
    navigate(`/home/${workspace.unique_identifier}/settings/integrations`);
  };

  const slackClick = async () => {
    if (!workspace?.id) return;

    setLoading(true);
    try {
      const connection = isConnected
        ? await integrationApp.integration(provider.key).openNewConnection()
        : await integrationApp.integration(provider.key).connect();

      const connReady = connection && ((connection as any).state === "READY" || (connection as any).id);

      if (connReady) {
        setLocalConnected(true);

        const connectionId = (connection as any).id || (connection as any).connectionId || "";

        // Ensure the connection has auth option set (required before flows can run)
        if (connectionId) {
          try {
            await integrationApp.connection(connectionId).patch({ authOptionKey: "oauth" } as any);
          } catch {
            // May already be set or not needed
          }
        }

        // Sync the connection to our backend DB
        try {
          await authorizedApiClient.post(
            `/${workspace.id}/integrations/slack/sync-connection`,
            { connection_id: connectionId, connection_name: (connection as any).name || "Slack" },
          );
        } catch {
          // webhook may handle it
        }

        // Trigger first-sync-for-users flow
        if (connectionId) {
          await Promise.allSettled([
            runConnectionFlow(integrationApp, connectionId, "sync-connection-details"),
            runConnectionFlow(integrationApp, connectionId, "first-sync-for-users"),
          ]);
        }

        notify({
          heading: "Integrated Slack",
          type: "success",
          messageList: ["Slack connected. User sync started."],
        });
        await delay(1000);
        void queryClient.invalidateQueries(["slack-integration", workspace?.id]);
      }
    } catch {
      notify({
        heading: "Integration Failed",
        type: "failure",
        messageList: ["Failed to integrate Slack with Hyperengage, please try again later"],
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <IntegrationDetailShell
      integrationName="Slack"
      connected={isConnected}
      credentialOk={isConnected}
      externalId={workspace?.id}
      reconnectLoading={loading}
      reconnectLabel={isConnected ? "Reconnect" : "Connect"}
      onReconnect={slackClick}
      onBack={handleBack}
    >
      <div className="rounded-lg border border-border p-4">
        <p className="text-[13px] text-foreground">
          Connect your Slack workspace to receive real-time account and signal notifications.
        </p>
        <p className="mt-1 text-[12px] text-muted-foreground">
          Slack permissions: channels read/write.
        </p>
        <div className="mt-4">
          <Button size="sm" className="h-7 text-[12px]" onClick={slackClick} disabled={loading}>
            {isConnected ? "Reconnect Slack" : "Log in with Slack"}
          </Button>
        </div>
      </div>
    </IntegrationDetailShell>
  );
};

export default SlackPage;
